/**
 * The look shared by the chart tiles: the Nivo theme (recessive grid and
 * axes, text in ink), number formatting that matches the table renderer,
 * series colours that follow the category rather than its position, and
 * the small layout sums (bar thickness, legend rows, label lengths) the
 * charts need once they know their size.
 */

import type { BarCommonProps, BarDatum, BarLegendProps } from "@nivo/bar"
import type { CartesianMarkerProps } from "@nivo/core"
import type { NumberFormatSpec, TableModel } from "utils/tableModel"
import { formatCell } from "utils/tableModel"
import { paletteColours } from "shared/ui/Charts/palettes"
import { leafLabel } from "features/Dashboard/runtime/modelAdapters"
import type {
  BarTile,
  DataTile,
  Measure,
} from "features/Dashboard/types/dashboardDocument"

/** Chart text ink; marks carry the series colours, text never does. */
export const CHART_INK = "#374151"
/** The tile surface; point rings are drawn in it. */
export const CHART_SURFACE = "#ffffff"
/** The target line and its label: neutral, so no series is mistaken for it. */
export const TARGET_INK = "#4b5563"
/** Opacity of the categories a cross-filter leaves out. */
export const DIM_ALPHA = 0.35
/** Colour for a category past the palette's eighth slot (never a cycled hue). */
export const OVERFLOW_COLOUR = "#9ca3af"
/** Thickest bar the charts draw, in px. */
export const MAX_BAR = 24
/** Rough width of one character of 11 px chart text, in px. */
export const CHAR_PX = 6.2
/** Height of one legend row, in px. */
export const LEGEND_ROW = 18

export const nivoTheme: NonNullable<BarCommonProps<BarDatum>["theme"]> = {
  text: { fontSize: 11, fill: CHART_INK, fontFamily: "inherit" },
  axis: {
    ticks: { line: { stroke: "#e5e7eb" }, text: { fill: "#4b5563" } },
    domain: { line: { stroke: "#e5e7eb" } },
  },
  grid: { line: { stroke: "#f3f4f6" } },
  crosshair: {
    line: {
      stroke: "#9ca3af",
      strokeWidth: 1,
      strokeOpacity: 1,
      strokeDasharray: "none",
    },
  },
  tooltip: {
    container: {
      background: "#ffffff",
      border: "1px solid #e5e7eb",
      borderRadius: 4,
      fontSize: 12,
      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    },
  },
  legends: { text: { fill: CHART_INK } },
}

const PLAIN: NumberFormatSpec = { decimals: 0, thousands: true, percent: false }

/**
 * A value in the table's format (percent with its decimals, thousands
 * separators), rounded the way the table rounds it; empty for null.
 */
export const formatValue = (
  v: number | null,
  format: NumberFormatSpec | null
): string => {
  if (v === null || !Number.isFinite(v)) return ""
  return formatCell({ raw: v, stat: "", kind: "count", format: format ?? PLAIN })
}

/** Decimal places `n` needs, up to 3 (0.25 needs 2, 10 needs none). */
const placesNeeded = (n: number): number => {
  for (let d = 0; d < 3; d++) {
    const shifted = n * 10 ** d
    if (Math.abs(shifted - Math.round(shifted)) < 1e-6) return d
  }
  return 3
}

/** An axis tick: the value's format with only the decimals the tick needs. */
export const formatTick = (
  v: number,
  format: NumberFormatSpec | null
): string => {
  if (!Number.isFinite(v)) return ""
  const spec = format ?? PLAIN
  const scaled = spec.percent ? v * 100 : v
  return formatValue(v, { ...spec, decimals: placesNeeded(scaled) })
}

export const withAlpha = (hex: string, alpha: number): string => {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex)
  if (!m) return hex
  return `rgba(${parseInt(m[1], 16)}, ${parseInt(m[2], 16)}, ${parseInt(m[3], 16)}, ${alpha})`
}

/** `#rrggbb` or `rgb(a)(...)` as RGB, a translucent colour laid over white. */
const toRgb = (colour: string): [number, number, number] | null => {
  const hex = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(colour)
  if (hex) return [1, 2, 3].map((i) => parseInt(hex[i], 16)) as [number, number, number]
  const fn =
    /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+)\s*)?\)$/i.exec(colour)
  if (!fn) return null
  const a = fn[4] === undefined ? 1 : Number(fn[4])
  return [1, 2, 3].map((i) => Number(fn[i]) * a + 255 * (1 - a)) as [
    number,
    number,
    number,
  ]
}

const luminance = ([r, g, b]: [number, number, number]): number => {
  const lin = (c: number) => {
    const s = c / 255
    return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b)
}

/** White or ink for a label set inside a fill, whichever reads better. */
export const labelInk = (fill: string): string => {
  const rgb = toRgb(fill)
  return rgb && luminance(rgb) < 0.22 ? "#ffffff" : "#1f2937"
}

/**
 * What a tile's colours are worked out from besides its last result: its
 * measures (rows of the down axis after its categories) and its rows'
 * shown names (its category display's renames).
 */
export interface ColourEntities {
  measures?: Measure[]
  labels?: Record<string, string>
}

/**
 * The column categories a bar leaves out: its Hide columns choice, which
 * applies only while its series come from the rows (the columns are then
 * its categories). Rows are hidden in the view, never here.
 */
export const hiddenColumns = (visual: BarTile["visual"]): string[] =>
  visual.seriesAxis === "down" ? visual.hiddenCategories : []

/** The entities of `tile` for `entityColours`. */
export const tileEntities = (tile: DataTile): ColourEntities => ({
  measures: tile.measures,
  labels: tile.categories.labels,
})

/**
 * Colour lookup for the rows or columns of one axis of a tile's charts.
 * Slots come from `raw`, the tile's last result, never from its view: on
 * the down axis the tile's measures first, in list order, so a measure
 * series never lands past the palette's end on a long scale; then the axis
 * leaves, with Totals after the rest. Hiding, reordering or renaming a row
 * never moves a slot. A row named in `colourMap` (the dashboard's colours)
 * takes that colour, matched by id, then by its label in `raw` (a
 * measure's own label), then by the name it is shown under. `mappedOf`
 * gives the mapped colour alone; `colourOf` falls back to the palette slot,
 * and a row past the palette's end gets the neutral overflow colour.
 */
export const colourLookup = (
  raw: TableModel,
  axis: "down" | "across",
  paletteId: string | undefined,
  colourMap: Record<string, string> = {},
  entities: ColourEntities = {}
): {
  colourOf: (id: string) => string
  mappedOf: (id: string) => string | undefined
} => {
  const ids: string[] = []
  const labels = new Map<string, string>()
  const add = (id: string, label: string): void => {
    if (labels.has(id)) return
    labels.set(id, label)
    ids.push(id)
  }
  if (axis === "down") {
    entities.measures?.forEach((m) => add(m.id, m.label))
  }
  raw[axis].leaves.forEach((leaf) => {
    if (!leaf.isTotal) add(leaf.id, leafLabel(leaf))
  })
  raw[axis].leaves.forEach((leaf) => {
    if (leaf.isTotal) add(leaf.id, leafLabel(leaf))
  })
  const colours = paletteColours(paletteId, ids.length)
  const slots = new Map(ids.map((id, i) => [id, colours[i] ?? OVERFLOW_COLOUR]))
  const shown = axis === "down" ? (entities.labels ?? {}) : {}
  const mapped = (key: string | undefined): string | undefined =>
    key !== undefined && Object.hasOwn(colourMap, key)
      ? colourMap[key]
      : undefined
  const mappedOf = (id: string): string | undefined =>
    mapped(id) ??
    mapped(labels.get(id)) ??
    mapped(Object.hasOwn(shown, id) ? shown[id] : undefined)
  return {
    colourOf: (id) => mappedOf(id) ?? slots.get(id) ?? OVERFLOW_COLOUR,
    mappedOf,
  }
}

/** The colour of each row or column of one axis (see `colourLookup`). */
export const entityColours = (
  raw: TableModel,
  axis: "down" | "across",
  paletteId: string | undefined,
  colourMap: Record<string, string> = {},
  entities: ColourEntities = {}
): ((id: string) => string) =>
  colourLookup(raw, axis, paletteId, colourMap, entities).colourOf

/**
 * A number typed in the chart's units (30 on a percentage chart is 30%) as
 * the value the chart plots (0.3).
 */
export const chartUnits = (
  v: number,
  format: NumberFormatSpec | null
): number => (format?.percent ? v / 100 : v)

/**
 * The value axis of a bar or line chart: `axisMin` and `axisMax` (in the
 * chart's units) where set. Otherwise the axis starts at 0 ("auto" when a
 * value is negative, so nothing is cut off) and ends where the values do,
 * or at the target when it lies above them. `nice` asks for rounded ends
 * once an end comes from the target. `sharedTop` (plotted units) is the end
 * the panels of a repeated tile share: the axis reaches it, whatever this
 * panel's own values.
 */
export const valueDomain = (
  values: (number | null)[],
  chart: { axisMin?: number; axisMax?: number; target?: { value: number } },
  format: NumberFormatSpec | null,
  sharedTop?: number
): { min: number | "auto"; max: number | "auto"; nice: boolean } => {
  const finite = values.filter((v): v is number => v !== null && Number.isFinite(v))
  const negative = finite.some((v) => v < 0)
  const own = finite.length > 0 ? Math.max(...finite) : 0
  const top = sharedTop !== undefined ? Math.max(own, sharedTop) : own
  const target =
    chart.target !== undefined ? chartUnits(chart.target.value, format) : null
  const min =
    chart.axisMin !== undefined
      ? chartUnits(chart.axisMin, format)
      : negative
        ? "auto"
        : 0
  const raiseToTarget =
    chart.axisMax === undefined && target !== null && target > top
  const max =
    chart.axisMax !== undefined
      ? chartUnits(chart.axisMax, format)
      : raiseToTarget
        ? target
        : sharedTop !== undefined
          ? top
          : "auto"
  return { min, max, nice: raiseToTarget }
}

/** `label` cut to `max` characters with an ellipsis. */
export const truncateLabel = (label: string, max: number): string =>
  label.length <= max
    ? label
    : `${label.slice(0, Math.max(1, max - 1)).trimEnd()}…`

/**
 * Band padding that keeps bars at most `MAX_BAR` px thick: a band of
 * `categories` groups of `barsPerGroup` bars along `length` px. Never under
 * 0.3 (the default gap), never over 0.9.
 */
export const barPadding = (
  length: number,
  categories: number,
  barsPerGroup: number,
  innerPadding: number
): number => {
  if (length <= 0 || categories <= 0 || barsPerGroup <= 0) return 0.3
  const band = MAX_BAR * barsPerGroup + innerPadding * (barsPerGroup - 1)
  const needed = (length - band * categories) / (length + band)
  return Math.min(0.9, Math.max(0.3, needed))
}

/** Axis labels along `length` px for `count` categories: rotate when crowded. */
export const categoryTicks = (
  length: number,
  count: number
): { rotation: number; maxChars: number } => {
  const step = count > 0 ? length / count : length
  if (count > 6 || step < 60) return { rotation: -30, maxChars: 14 }
  return { rotation: 0, maxChars: Math.max(4, Math.floor((step - 6) / CHAR_PX)) }
}

export interface LegendItem {
  id: string
  label: string
  color: string
}

/** Rows a bottom legend of `items` takes across `width` px. */
export const legendLayout = (
  width: number,
  items: LegendItem[]
): { itemWidth: number; perRow: number; rows: number; maxChars: number } => {
  const longest = Math.max(0, ...items.map((i) => i.label.length))
  const itemWidth = Math.min(160, Math.max(64, Math.ceil(longest * CHAR_PX + 24)))
  const perRow = Math.max(1, Math.floor(width / itemWidth))
  return {
    itemWidth,
    perRow,
    rows: Math.ceil(items.length / perRow),
    maxChars: Math.floor((itemWidth - 24) / CHAR_PX + 1e-9),
  }
}

/**
 * Nivo legends for `items` as rows under the plot, the last row on the
 * bottom edge of a `marginBottom` px margin. Colours are passed in full, so
 * a dimmed first category never greys its series' key.
 */
export const bottomLegends = (
  width: number,
  items: LegendItem[],
  marginBottom: number,
  symbolShape?: BarLegendProps["symbolShape"]
): BarLegendProps[] => {
  const { itemWidth, perRow, rows, maxChars } = legendLayout(width, items)
  return Array.from({ length: rows }, (_, r) => ({
    dataFrom: "keys" as const,
    data: items.slice(r * perRow, (r + 1) * perRow).map((i) => ({
      id: i.id,
      label: truncateLabel(i.label, maxChars),
      color: i.color,
    })),
    anchor: "bottom" as const,
    direction: "row" as const,
    translateY: marginBottom - 2 - (rows - 1 - r) * LEGEND_ROW,
    itemWidth,
    itemHeight: 14,
    symbolSize: 10,
    ...(symbolShape ? { symbolShape } : {}),
  }))
}

/** Each category's positive and negative totals: how far its stack reaches. */
export const stackEnds = (
  values: (number | null)[][],
  count: number
): number[] =>
  Array.from({ length: count }, (_, i) => {
    let up = 0
    let down = 0
    values.forEach((row) => {
      const v = row[i] ?? 0
      if (v > 0) up += v
      else down += v
    })
    return [up, down]
  }).flat()

/** Width of the legend column beside a chart, in px. */
export const RIGHT_LEGEND = 140

/** A Nivo legend column right of the plot, labels cut to fit. */
export const rightLegends = (
  items: LegendItem[],
  symbolShape?: BarLegendProps["symbolShape"]
): BarLegendProps[] => [
  {
    dataFrom: "keys" as const,
    anchor: "right" as const,
    direction: "column" as const,
    translateX: RIGHT_LEGEND - 20,
    itemWidth: RIGHT_LEGEND - 30,
    itemHeight: 16,
    symbolSize: 10,
    data: items.map((i) => ({
      id: i.id,
      label: truncateLabel(i.label, 16),
      color: i.color,
    })),
    ...(symbolShape ? { symbolShape } : {}),
  },
]

/** The dashed target line of a bar or line chart, labelled at its end. */
export const targetMarker = (
  axis: "x" | "y",
  value: number,
  label: string
): CartesianMarkerProps => ({
  axis,
  value,
  legend: label,
  legendPosition: axis === "y" ? "top-right" : "bottom-right",
  legendOrientation: "horizontal",
  lineStyle: { stroke: TARGET_INK, strokeWidth: 1.5, strokeDasharray: "5 4" },
  textStyle: { fill: TARGET_INK, fontSize: 11 },
})

/** Extra bottom margin a bottom legend of `items` needs (none for one series). */
export const legendHeight = (width: number, items: LegendItem[]): number =>
  items.length > 1 ? legendLayout(width, items).rows * LEGEND_ROW + 6 : 0

/** True when the viewer asked the system for less motion. */
export const prefersReducedMotion = (): boolean =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  Boolean(window.matchMedia("(prefers-reduced-motion: reduce)")?.matches)
